import React, { lazy} from "react";

import "./app.scss";
import "./jquery-polyfill";

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers';

import { LoginPage } from "./pages/LoginPage";
import { SiHeader } from "./components/navigation/SiHeader";
import { t } from "./i18n";
import { SiFooter } from "./components/SiFooter";
import { SiAppAdminModal } from "./components/SiAppAdminModal";
import { SiAppAdminSnackbar } from "./components/SiAppAdminSnackbar";
import { inject, observer } from "mobx-react";
import { withRouter, RouteComponentProps } from "./utils/withRouter";

import { Route, Routes } from "react-router-dom";
import { UserStore } from "./stores/UserStore";
import { LivestreamStore } from "./stores/LivestreamStore";
import { If } from "./components/If";
import { Loader } from "./components/Loader"

import { AuthenticatedRoute } from "./components/navigation/AuthenticatedRoute";
import usePageViews from "./hooks/usePageViews";
import { appHistory } from "./appHistory";

const DashboardPage = lazy(() => import("./pages/DashboardPage"))
const PasswordResetPage = lazy(() => import("./pages/PasswordResetPage"))
const ContactPage = lazy(() => import("./pages/ContactPage"))
const EmailLoginPage = lazy(() => import("./pages/EmailLoginPage"))
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"))
const LandingPage = lazy(() => import("./pages/LandingPage"))
const VideoPage = lazy(() => import("./pages/VideoPage"))
const WorkshopsPage = lazy(() => import("./pages/WorkshopsPage"))
const ResourcesPage = lazy(() => import("./pages/ResourcesPage"))
const WatchlistPage = lazy(() => import("./pages/WatchlistPage"))
const HistoryPage = lazy(() => import("./pages/HistoryPage"))
const ReportsPage = lazy(() => import("./pages/ReportsPage"))
const VerifyAccountPage = lazy(() => import("./pages/VerifyAccountPage"))
const DenyResetPage = lazy(() => import("./pages/DenyResetPage"))
const ProfilePage = lazy(() => import("./pages/ProfilePage"))
const SearchResultsPage = lazy(() => import("./pages/SearchResultsPage"))
const VideosPage = lazy(() => import("./pages/VideosPage"))
const OrganizationContent = lazy(() => import("./pages/OrganizationContent"))
const LivestreamPage = lazy(() => import("./pages/LivestreamPage"))
const LivestreamRecordingPage = lazy(() => import("./pages/LivestreamRecordingPage"))

type IAppProps = {
  userStore?: UserStore
  livestreamStore?: LivestreamStore
} & RouteComponentProps;

const RouteChangeListener = () => {
  usePageViews();
  return null;
}

export const AppImpl = inject("userStore", "livestreamStore")(observer(class AppImpl extends React.Component<IAppProps> {

  constructor(props: IAppProps) {
    super(props);
    const { userStore, livestreamStore } = this.props;
    if (!userStore) {
      throw new Error("userStore is not defined");
    }
    if (!livestreamStore) {
      throw new Error("livestreamStore is not defined");
    }
    userStore.loadUser().then(() => {
      if (userStore.isAuthenticated) {
        userStore.updateOrganizationTheme();
        livestreamStore.checkEventsStatus(true)
        const { location } = this.props.router
        if (location.pathname === "/") {
          userStore.redirectHome()
        }
      }
    });
  }
  areHeaderAndFooterVisible(): boolean {
    const { location } = this.props.router
    const headerlessRoutes = ['/login', '/password-reset', '/email-login', '/verify-account', '/live-recordings', '/deny-reset']
    return (
      !headerlessRoutes.some(function (v) { return location.pathname.includes(v) })
    );
  }

  render() {
    const scrollTop = () => {
      window.scrollTo({ top: 0 });
    };
    const { userStore } = this.props;

    if (!userStore) {
      throw new Error("userStore is undefined");
    }

    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <main>
          <router-view>
            <If predicate={this.areHeaderAndFooterVisible()}>
              <SiHeader
                authenticated={userStore.isAuthenticated}
                logout={() => userStore.logout()}
              />
            </If>
            <RouteChangeListener />
            <React.Suspense fallback={<Loader loading={true} />}>
              <Routes>
                <Route path="/login" element={<LoginPage />} />

                <Route
                  path="/password-reset"
                  element={<PasswordResetPage />}
                />

                <Route
                  path="/deny-reset"
                  element={<DenyResetPage />}
                />

                <Route path="/contact" element={<ContactPage />} />

                <Route path="/email-login" element={<EmailLoginPage />} />

                <Route path="/landing-page/:id" element={<LandingPage />} />

                <Route path="/" element={
                  <AuthenticatedRoute>
                    <DashboardPage />
                  </AuthenticatedRoute>
                } />

                <Route path="/dashboard" element={
                  <AuthenticatedRoute>
                    <DashboardPage />
                  </AuthenticatedRoute>
                } />

                <Route
                  path="/privacy-policy"
                  element={<PrivacyPolicyPage />}
                />

                <Route path="/resources" element={
                  <AuthenticatedRoute>
                    <ResourcesPage />
                  </AuthenticatedRoute>
                } />

                <Route path="/watchlist" element={
                  <AuthenticatedRoute>
                    <WatchlistPage />
                  </AuthenticatedRoute>
                } />

                <Route path="/history" element={
                  <AuthenticatedRoute>
                    <HistoryPage />
                  </AuthenticatedRoute>
                } />

                <Route path="/reports" element={
                  <AuthenticatedRoute>
                    <ReportsPage />
                  </AuthenticatedRoute>
                } />

                <Route
                  path="/verify-account"
                  element={<VerifyAccountPage />}
                />

                <Route path="/video/:id" element={
                  <AuthenticatedRoute>
                    <VideoPage />
                  </AuthenticatedRoute>
                } />

                <Route path="/workshops/:id" element={
                  <AuthenticatedRoute>
                    <WorkshopsPage />
                  </AuthenticatedRoute>
                } />

                <Route path="/search-results/:query">
                  <Route index element={
                    <AuthenticatedRoute>
                      <SearchResultsPage />
                    </AuthenticatedRoute>
                  } />
                  <Route path=":categoryId" element={
                    <AuthenticatedRoute>
                      <SearchResultsPage />
                    </AuthenticatedRoute>
                  } />
                </Route>

                <Route path="/profile" element={
                  <AuthenticatedRoute>
                    <ProfilePage />
                  </AuthenticatedRoute>
                } />

                <Route path="/videos" >
                  <Route index element={
                    <AuthenticatedRoute>
                      <VideosPage />
                    </AuthenticatedRoute>
                  } />
                  <Route path=":category" element={
                    <AuthenticatedRoute>
                      <VideosPage />
                    </AuthenticatedRoute>
                  } />
                </Route>

                <Route path="/mydistrict" element={
                  <AuthenticatedRoute>
                    <OrganizationContent
                      contentType="district-content"
                    />
                  </AuthenticatedRoute>
                } />

                <Route path="/myschool" element={
                  <AuthenticatedRoute>
                    <OrganizationContent
                      contentType="school-content"
                    />
                  </AuthenticatedRoute>
                } />

                <Route path="/livestream" element={
                  <AuthenticatedRoute>
                    <LivestreamPage />
                  </AuthenticatedRoute>
                } />

                <Route path="/live-recordings/:directLink" element={
                  <LivestreamRecordingPage />
                } />

              </Routes>
            </React.Suspense>

            <If predicate={this.areHeaderAndFooterVisible()}>
              <SiFooter authenticated={userStore.isAuthenticated} />
            </If>
          </router-view>
        </main>
        <SiAppAdminModal />
        <SiAppAdminSnackbar />
        <span className="dummy-link back-to-top" onClick={scrollTop}>
          <i className="material-icons">arrow_drop_up</i>
          {t("Back To Top")}
        </span>

        <footer className="main">
          {t("Copyright")} &copy; {t("Successful Innovations")}.
          {t(" All Rights Reserved")}.
        </footer>
      </LocalizationProvider>
    );
  }
}));

export const App = withRouter(AppImpl);
